// Sentiment Donut Chart Component
// Displays sentiment distribution as a visual donut chart

import React from 'react';

/**
 * SentimentDonutChart Component
 *
 * Displays positive/neutral/negative sentiment distribution as a donut chart
 *
 * @param {Object} sentimentDistribution - { positive, neutral, negative }
 */
function SentimentDonutChart({ sentimentDistribution }) {
  const { positive = 0, neutral = 0, negative = 0 } = sentimentDistribution || {};
  const total = positive + neutral + negative;

  if (total === 0) {
    return <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>No sentiment data</div>;
  }

  // Calculate percentages
  const positivePercent = (positive / total) * 100;
  const neutralPercent = (neutral / total) * 100;
  const negativePercent = (negative / total) * 100;

  // SVG donut chart parameters
  const size = 120;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate stroke dash offsets for each segment
  const positiveLength = (positivePercent / 100) * circumference;
  const neutralLength = (neutralPercent / 100) * circumference;
  const negativeLength = (negativePercent / 100) * circumference;

  // Starting positions for each segment
  const positiveOffset = 0;
  const neutralOffset = positiveLength;
  const negativeOffset = positiveLength + neutralLength;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
      {/* Donut Chart SVG */}
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />

        {/* Positive segment */}
        {positive > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#10b981"
            strokeWidth={strokeWidth}
            strokeDasharray={`${positiveLength} ${circumference}`}
            strokeDashoffset={-positiveOffset}
            strokeLinecap="round"
          />
        )}

        {/* Neutral segment */}
        {neutral > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#6b7280"
            strokeWidth={strokeWidth}
            strokeDasharray={`${neutralLength} ${circumference}`}
            strokeDashoffset={-neutralOffset}
            strokeLinecap="round"
          />
        )}

        {/* Negative segment */}
        {negative > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#ef4444"
            strokeWidth={strokeWidth}
            strokeDasharray={`${negativeLength} ${circumference}`}
            strokeDashoffset={-negativeOffset}
            strokeLinecap="round"
          />
        )}

        {/* Center text showing total */}
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fontSize: '1.25rem', fontWeight: 'bold', fill: '#1f2937' }}
        >
          {total}
        </text>
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
        {positive > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#10b981' }} />
            <span style={{ color: '#374151' }}>
              Positive: <strong>{positive}</strong> ({positivePercent.toFixed(0)}%)
            </span>
          </div>
        )}
        {neutral > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#6b7280' }} />
            <span style={{ color: '#374151' }}>
              Neutral: <strong>{neutral}</strong> ({neutralPercent.toFixed(0)}%)
            </span>
          </div>
        )}
        {negative > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
            <span style={{ color: '#374151' }}>
              Negative: <strong>{negative}</strong> ({negativePercent.toFixed(0)}%)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SentimentDonutChart;
